import type {
  SeasonDateRange,
  SeasonalPricingConfig,
  SeasonalRates,
  SeasonType,
  SeasonInfo,
  PriceBreakdown,
  ResidenceType,
  SupportedLanguage,
} from '../types';

/**
 * Default high season ranges for each residence type
 */
export const DEFAULT_SEASON_RANGES: Record<ResidenceType, SeasonDateRange[]> = {
  summer: [
    {
      id: 'summer-main',
      startMonth: 6,
      startDay: 15,
      endMonth: 9,
      endDay: 15,
      label: { de: 'Sommersaison', en: 'Summer Season' },
    },
  ],
  winter: [
    {
      id: 'winter-ski',
      startMonth: 12,
      startDay: 15,
      endMonth: 3,
      endDay: 31,
      label: { de: 'Skisaison', en: 'Ski Season' },
    },
    {
      id: 'summer-hiking',
      startMonth: 7,
      startDay: 1,
      endMonth: 8,
      endDay: 31,
      label: { de: 'Wandersaison', en: 'Hiking Season' },
    },
  ],
  year_round_peak: [
    {
      id: 'winter-holidays',
      startMonth: 12,
      startDay: 20,
      endMonth: 1,
      endDay: 6,
      label: { de: 'Winterferien', en: 'Winter Holidays' },
    },
    {
      id: 'easter',
      startMonth: 3,
      startDay: 25,
      endMonth: 4,
      endDay: 15,
      label: { de: 'Osterferien', en: 'Easter Holidays' },
    },
    {
      id: 'summer-peak',
      startMonth: 7,
      startDay: 1,
      endMonth: 8,
      endDay: 31,
      label: { de: 'Sommerferien', en: 'Summer Holidays' },
    },
  ],
};

/**
 * Check if a date falls within a season range
 * Handles year crossover (e.g., Dec 15 - Mar 31)
 */
export function isDateInRange(date: Date, range: SeasonDateRange): boolean {
  const month = date.getMonth() + 1; // 1-12
  const day = date.getDate();

  const startValue = range.startMonth * 100 + range.startDay;
  const endValue = range.endMonth * 100 + range.endDay;
  const dateValue = month * 100 + day;

  // Normal range (e.g., Jun 15 - Sep 15)
  if (startValue <= endValue) {
    return dateValue >= startValue && dateValue <= endValue;
  }

  // Year crossover range (e.g., Dec 15 - Mar 31)
  return dateValue >= startValue || dateValue <= endValue;
}

/**
 * Check if a date is a weekend (Saturday or Sunday)
 */
export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/**
 * Get the season info for a specific date
 */
export function getSeasonForDate(
  date: Date,
  config: SeasonalPricingConfig
): SeasonInfo {
  for (const range of config.highSeasonRanges) {
    if (isDateInRange(date, range)) {
      return {
        type: 'high',
        label: range.label?.en,
        rates: config.highSeasonRates,
      };
    }
  }

  return {
    type: 'low',
    rates: config.lowSeasonRates,
  };
}

/**
 * Get the nightly rate for a specific date
 */
export function getNightlyRate(
  date: Date,
  config: SeasonalPricingConfig,
  isFamily: boolean
): number {
  const seasonInfo = getSeasonForDate(date, config);
  const rates = seasonInfo.rates;

  if (isFamily) {
    return rates.familyRate;
  }

  return isWeekend(date) ? rates.weekendRate : rates.weekdayRate;
}

/**
 * Calculate a detailed price breakdown for a booking
 */
export function calculatePriceBreakdown(
  startDate: Date,
  endDate: Date,
  config: SeasonalPricingConfig,
  isFamily: boolean
): PriceBreakdown {
  const perNightDetails: PriceBreakdown['perNightDetails'] = [];
  let totalPrice = 0;
  let highSeasonNights = 0;
  let lowSeasonNights = 0;
  let weekdayNights = 0;
  let weekendNights = 0;

  const currentDate = new Date(startDate);

  // Iterate through each night (not including checkout day)
  while (currentDate < endDate) {
    const seasonInfo = getSeasonForDate(currentDate, config);
    const weekend = isWeekend(currentDate);
    const rate = getNightlyRate(currentDate, config, isFamily);

    perNightDetails.push({
      date: new Date(currentDate),
      season: seasonInfo.type,
      isWeekend: weekend,
      rate,
    });

    totalPrice += rate;

    if (seasonInfo.type === 'high') {
      highSeasonNights++;
    } else {
      lowSeasonNights++;
    }

    if (weekend) {
      weekendNights++;
    } else {
      weekdayNights++;
    }

    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return {
    totalPrice,
    nights: perNightDetails.length,
    highSeasonNights,
    lowSeasonNights,
    weekdayNights,
    weekendNights,
    perNightDetails,
  };
}

/**
 * Format a season range for display
 */
export function formatSeasonRange(
  range: SeasonDateRange,
  language: SupportedLanguage
): string {
  const months = language === 'de'
    ? ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const startMonth = months[range.startMonth - 1];
  const endMonth = months[range.endMonth - 1];

  return `${range.startDay} ${startMonth} - ${range.endDay} ${endMonth}`;
}

/**
 * Format all season ranges for display
 */
export function formatSeasonRanges(
  ranges: SeasonDateRange[],
  language: SupportedLanguage
): string[] {
  return ranges.map(range => {
    const dateStr = formatSeasonRange(range, language);
    const label = range.label?.[language];
    return label ? `${dateStr} (${label})` : dateStr;
  });
}

/**
 * Get the current season based on today's date
 */
export function getCurrentSeason(config: SeasonalPricingConfig): SeasonInfo {
  return getSeasonForDate(new Date(), config);
}

/**
 * Get human-readable residence type label
 */
export function getResidenceTypeLabel(
  type: ResidenceType,
  language: SupportedLanguage
): string {
  const labels: Record<ResidenceType, Record<SupportedLanguage, string>> = {
    summer: { de: 'Sommerresidenz', en: 'Summer Residence' },
    winter: { de: 'Winterresidenz', en: 'Winter Residence' },
    year_round_peak: { de: 'Ganzjahresresidenz', en: 'Year-round Peak Residence' },
  };
  return labels[type][language];
}

/**
 * Get human-readable season type label
 */
export function getSeasonTypeLabel(
  type: SeasonType,
  language: SupportedLanguage
): string {
  const labels: Record<SeasonType, Record<SupportedLanguage, string>> = {
    high: { de: 'Hochsaison', en: 'High Season' },
    low: { de: 'Nebensaison', en: 'Low Season' },
  };
  return labels[type][language];
}

/**
 * Create default seasonal rates
 */
export function createDefaultRates(baseRate: number): SeasonalRates {
  return {
    weekdayRate: baseRate,
    weekendRate: baseRate,
    weekRate: baseRate,
    familyRate: Math.round(baseRate * 0.7),
    guestRate: baseRate,
  };
}

/**
 * Create a default seasonal pricing config for a residence type
 */
export function createDefaultSeasonalConfig(
  residenceType: ResidenceType,
  highSeasonBaseRate: number = 200,
  lowSeasonBaseRate: number = 150
): SeasonalPricingConfig {
  return {
    residenceType,
    highSeasonRanges: DEFAULT_SEASON_RANGES[residenceType].map(range => ({
      ...range,
      id: `${range.id}-${Date.now()}`,
    })),
    highSeasonRates: createDefaultRates(highSeasonBaseRate),
    lowSeasonRates: createDefaultRates(lowSeasonBaseRate),
  };
}

/**
 * Generate a unique ID for season ranges
 */
export function generateSeasonRangeId(): string {
  return `season-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get the nightly rate for a booking based on the season of the start date.
 */
export function getWeeklyRate(
  startDate: Date,
  config: SeasonalPricingConfig
): number {
  const seasonInfo = getSeasonForDate(startDate, config);
  return seasonInfo.rates.weekRate;
}
